<template>
    <div :class="['category-tree-item', levelClass]">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow">
            <div class="p-4">
                <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-3 flex-1">
                        <!-- Expand/Collapse Button (if has children) -->
                        <button
                            v-if="category.children && category.children.length > 0"
                            @click="toggleExpanded"
                            class="flex-shrink-0 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        >
                            <svg
                                class="w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform"
                                :class="{ 'rotate-90': isExpanded }"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                        <div v-else class="w-6"></div>

                        <!-- Category Info -->
                        <div class="flex-1 min-w-0">
                            <div class="flex items-center space-x-2">
                                <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {{ category.name }}
                                </h3>
                                <span
                                    v-if="!category.is_active"
                                    class="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400"
                                >
                                    Inactive
                                </span>
                            </div>
                            <p v-if="category.description" class="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                                {{ category.description }}
                            </p>
                            <div class="mt-1 flex items-center space-x-3 text-xs text-gray-500 dark:text-gray-400">
                                <span class="flex items-center">
                                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                    {{ category.assets_count || 0 }} asset{{ (category.assets_count || 0) !== 1 ? 's' : '' }}
                                </span>
                                <span v-if="category.children && category.children.length > 0" class="flex items-center">
                                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                                    </svg>
                                    {{ category.children.length }} subcategor{{ category.children.length !== 1 ? 'ies' : 'y' }}
                                </span>
                            </div>
                        </div>

                        <!-- Actions -->
                        <div class="flex items-center space-x-2">
                            <Link
                                :href="route('settings.maintenance.asset-categories.show', category.id)"
                                class="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                            >
                                View
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Children (if expanded) -->
        <div
            v-if="category.children && category.children.length > 0 && isExpanded"
            class="mt-3 ml-6 pl-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-3"
        >
            <CategoryTree
                v-for="child in category.children"
                :key="child.id"
                :category="child"
                :level="level + 1"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Link } from '@inertiajs/vue3';
import route from '@/Utils/route';

const props = defineProps({
    category: {
        type: Object,
        required: true,
    },
    level: {
        type: Number,
        default: 0,
    },
});

const isExpanded = ref(props.level < 2); // Auto-expand first 2 levels

const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value;
};

const levelClass = computed(() => {
    return props.level === 0 ? 'mb-4' : '';
});
</script>
